// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type UserRow, useUserByPublicIdQuery } from "@/api/hooks/use-users";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Button } from "@/components/ui/button";
import { ChangeStoreDialog } from "@/features/iam/change-store-dialog";
import { DeleteUserDialog } from "@/features/iam/delete-user-dialog";
import { ToggleUserActiveDialog } from "@/features/iam/toggle-user-active-dialog";
import { UserActionsMenu } from "@/features/iam/user-actions-menu";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/iam/users/$publicId")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.iamUsers}>
      <UserDetailLayout />
    </RequirePermission>
  ),
});

/**
 * Chrome shared by the user's read page (`/iam/users/$publicId`) and its edit page
 * (`/iam/users/$publicId/edit`): the identity pill row, the actions menu and its dialogs.
 * Keeping it in the layout means the header stays mounted when moving between the two.
 */
function UserDetailLayout() {
  const { publicId } = Route.useParams();
  const { t } = useTranslation("back-office");
  const { user: currentUser, hasPermission } = useAuth();
  const matchRoute = useMatchRoute();
  const { data, isLoading, isError } = useUserByPublicIdQuery(publicId);

  const isAdmin = currentUser?.groups.includes("admin") ?? false;
  // The button that opens the edit page is hidden once we're there — the email breadcrumb is the
  // way back. A deleted user is read-only, so it never gets one.
  const isEditPage = !!matchRoute({ to: "/iam/users/$publicId/edit" });
  const canEdit = hasPermission(PAGE_PERMISSIONS.iamUsersEdit) && !data?.user.isDeleted;
  const email = data?.user.email ?? publicId;

  const breadcrumbs = useMemo(
    () =>
      isEditPage
        ? [
            { label: t("users.title"), to: "/iam/users" },
            { label: email, to: `/iam/users/${publicId}`, bold: true },
          ]
        : [{ label: t("users.title"), to: "/iam/users" }],
    [t, isEditPage, email, publicId],
  );

  const [toggleUser, setToggleUser] = useState<UserRow | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserRow | null>(null);
  const [changeStoreUser, setChangeStoreUser] = useState<{
    publicId: string;
    email: string;
    fullName?: string;
    defaultStoreName: string;
  } | null>(null);

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <PageHeader title={publicId} breadcrumbs={[{ label: t("users.title"), to: "/iam/users" }]} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  const { user, defaultStore } = data;

  return (
    <div>
      <PageHeader
        title={isEditPage ? t("buttons.edit") : user.email}
        subtitle={
          <>
            <IdPill id={user.userId} />
            <StatusIndicator active={user.isActive} deleted={user.isDeleted} />
            {!user.isDeleted && (
              <div className="ml-auto -my-1.5 flex items-center gap-2">
                {/* 30px rather than the `sm` 32px — sits a touch tighter in the pill row */}
                {canEdit && !isEditPage && (
                  <Button asChild size="sm" className="h-7.5">
                    <Link to="/iam/users/$publicId/edit" params={{ publicId }} search={{}}>
                      <Pencil className="h-4 w-4" />
                      {t("buttons.edit")}
                    </Link>
                  </Button>
                )}
                <UserActionsMenu
                  isActive={user.isActive}
                  isAdmin={isAdmin}
                  isSelf={currentUser?.publicId === user.publicId}
                  onChangeStore={() => {
                    setChangeStoreUser({
                      publicId: user.publicId,
                      email: user.email,
                      fullName: user.fullName,
                      defaultStoreName: defaultStore?.storeName ?? "",
                    });
                  }}
                  onToggleActive={() => setToggleUser(user)}
                  onDelete={() => setDeleteUser(user)}
                />
              </div>
            )}
          </>
        }
        breadcrumbs={breadcrumbs}
      />

      <Outlet />

      <ChangeStoreDialog user={changeStoreUser} onClose={() => setChangeStoreUser(null)} />
      <ToggleUserActiveDialog user={toggleUser} onClose={() => setToggleUser(null)} />
      <DeleteUserDialog user={deleteUser} onClose={() => setDeleteUser(null)} />
    </div>
  );
}
